import { MouseEvent, useContext, useState } from 'react';

import { mdiDotsHorizontal, mdiPencil, mdiDelete, mdiEmail } from '@mdi/js';
import Icon from '@mdi/react';
import { Avatar, IconButton, Menu, MenuItem, TableCell, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RosterTableData } from './types';
import { RosterContext } from '../../context/RosterContext';
import stringToColor from '../../utils/stringToColor';

const BodyCell = styled(TableCell)`
    padding: 0.25rem;
`;

const UserDiv = styled.div`
    display: flex;
    align-items: center;
    margin-left: 8px;
`;

const NameDiv = styled.div`
    margin-left: 10px;
`;

const EmailDiv = styled.div`
    margin-left: 10px;
`;

const RoleDiv = styled.div`
    margin-left: 10px;
`;

const GroupsDiv = styled.div`
    margin-left: 10px;
    max-width: 150px;
`;

const MenuIcon = styled(Icon)`
    max-width: 48px;
    color: ${(props) => props.theme.colors.primary.main};
    height: 36px;
`;

const MenuItemIcon = styled(Icon)`
    max-width: 20px;
    height: 20px;
`;

export interface RosterTableRowProps {
    row: RosterTableData;
    hasStudentGroups: boolean;
    onRowClick: (event: MouseEvent<HTMLButtonElement>, studentId: number) => void;
}

const RosterTableRow: React.FC<RosterTableRowProps> = ({ row, hasStudentGroups, onRowClick }) => {
    const {
        student_id: studentId,
        user_image: userImage,
        user_name: userName,
        first_name: firstName,
        last_name: lastName,
        email,
        role,
        student_group_names: studentGroupNames
    } = row;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { setOpenEditModal, setOpenDeleteModal, myDetails } = useContext(RosterContext);
    const open = Boolean(anchorEl);

    const { t } = useTranslation('roster');

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleEmailClick = () => {
        setAnchorEl(null);
        window.open(`mailto:${email}`);
    };
    const handleRemoveClick = async (e) => {
        onRowClick(e, studentId);
        setAnchorEl(null);
        setOpenDeleteModal(true);
    };
    const handleEditClick = (e) => {
        onRowClick(e, studentId);
        setAnchorEl(null);
        setOpenEditModal(true);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const renderDelete = () => {
        const isMeAndTeacherRole = myDetails?.student_id === studentId && myDetails.role === 'teacher';

        if (isMeAndTeacherRole) {
            return null;
        }

        return (
            <MenuItem data-testid={'delete'} onClick={handleRemoveClick}>
                <MenuItemIcon path={mdiDelete} />
                {t('Delete')}
            </MenuItem>
        );
    };

    return (
        <TableRow>
            <BodyCell>
                <UserDiv>
                    <Avatar
                        alt={userName}
                        src={userImage || './'}
                        sx={{ bgcolor: stringToColor(`${firstName} ${lastName}`) }}
                    >
                        {firstName && firstName[0] ? firstName[0].toUpperCase() : ''}
                        {lastName && lastName[0] ? lastName[0].toUpperCase() : ''}
                    </Avatar>
                    <NameDiv data-testid={'name'}>{userName}</NameDiv>
                </UserDiv>
            </BodyCell>
            <BodyCell align={'left'}>
                <EmailDiv data-testid={'email'}>{email}</EmailDiv>
            </BodyCell>
            <BodyCell align={'left'}>
                <RoleDiv data-testid={'role'}>{t(`roles.${role}`)}</RoleDiv>
            </BodyCell>
            {hasStudentGroups ? (
                <BodyCell align={'left'}>
                    <GroupsDiv data-testid={'student_group_names'}>{studentGroupNames?.join(', ') ?? ''}</GroupsDiv>
                </BodyCell>
            ) : null}
            <BodyCell align={'center'}>
                <IconButton data-testid={'actions'} onClick={handleClick}>
                    <MenuIcon path={mdiDotsHorizontal} />
                </IconButton>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem data-testid={'edit'} onClick={handleEditClick}>
                        <MenuItemIcon path={mdiPencil} />
                        {t('Edit')}
                    </MenuItem>
                    {renderDelete()}
                    {false && (
                        <MenuItem onClick={handleEmailClick} hidden={true}>
                            <MenuItemIcon path={mdiEmail} />
                            {t('Email')}
                        </MenuItem>
                    )}
                </Menu>
            </BodyCell>
        </TableRow>
    );
};

export default RosterTableRow;
